import { createEntityHooks } from '../core/createEntityHooks'
import type { BaseDocument } from '../core/types'

/**
 * TaskContent document interface
 */
export interface TaskContentDocument extends BaseDocument {
  taskId: string
  content: string
  format: string
  length: number
  plainText?: string | null
  version: number
  createdOn: number
  updatedOn: number
  deletedOn?: number | null
}

/**
 * TaskContent-specific hooks
 */
const hooks = createEntityHooks<TaskContentDocument>('taskcontent')

// Wrapper to match naming convention - queries by taskId, not id
export const useTaskContent = (taskId: string | undefined) => {
  const { data, isLoading } = hooks.useEntities(
    taskId ? { taskId, isDeleted: false } : undefined,
    { enabled: !!taskId },
  )
  // Return first result since it's 1:1 relationship
  return { content: data[0] || null, isLoading }
}

/**
 * Content rows for many tasks at once (board card previews). The entity hook
 * passes filter values straight into the RxDB selector, so the $in is scoped.
 */
export const useTaskContentsForTasks = (taskIds: Array<string>) => {
  const { data, isLoading } = hooks.useEntities(
    { taskId: { $in: taskIds } } as unknown as Partial<TaskContentDocument>,
    { enabled: taskIds.length > 0 },
  )
  return { contents: taskIds.length > 0 ? data : [], isLoading }
}

// Wrapper to provide entity-specific method names
export const useTaskContentMutations = () => {
  const { create, update, delete: deleteEntity } = hooks.useMutations()
  return {
    createTaskContent: create,
    updateTaskContent: update,
    deleteTaskContent: deleteEntity,
  }
}
